import React, { useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { authAPI } from '../utils/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Reset link is missing or invalid.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to reset password. Please request a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_83ef92c5-3c5d-4a35-b89a-364f4b1c35a2/artifacts/htwqujap_1000X1000.png"
            alt="Ami Video"
            className="h-24 mx-auto mb-6 dark:brightness-0 dark:invert"
          />
          <h1 className="text-3xl font-bold tracking-tight mb-2">Ami Video</h1>
          <p className="text-xs text-muted-foreground tracking-[0.25em] uppercase">
            Photography is Poetry
          </p>
        </div>

        {success ? (
          <div className="text-center space-y-6" data-testid="reset-password-success">
            <h2 className="text-2xl font-semibold tracking-tight">Password updated</h2>
            <p className="text-sm text-muted-foreground">
              You can now sign in with your new password. Redirecting you to the login page...
            </p>
            <Link to="/login" className="inline-block text-sm text-foreground hover:opacity-70 transition-opacity">
              Go to sign in now
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-semibold tracking-tight mb-1">Set a new password</h2>
              <p className="text-sm text-muted-foreground">Choose a strong password you'll remember</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-2xl text-sm" data-testid="reset-password-error">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-muted-foreground mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3.5 pr-11 rounded-2xl bg-secondary border border-transparent text-foreground focus:outline-none focus:bg-card focus:border-black/20 focus:ring-2 focus:ring-black/10 transition-all"
                    placeholder="At least 6 characters"
                    required
                    minLength={6}
                    data-testid="reset-password-new-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                    data-testid="reset-password-toggle-visibility"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-muted-foreground mb-1.5">
                  Confirm Password
                </label>
                <input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-2xl bg-secondary border border-transparent text-foreground focus:outline-none focus:bg-card focus:border-black/20 focus:ring-2 focus:ring-black/10 transition-all"
                  placeholder="Re-enter password"
                  required
                  minLength={6}
                  data-testid="reset-password-confirm-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground px-6 py-3.5 rounded-full font-medium hover:bg-black/90 transition-all active:scale-[0.98] disabled:opacity-50"
                data-testid="reset-password-submit-button"
              >
                {loading ? 'Updating…' : 'Update Password'}
              </button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-login-link">
                  Back to sign in
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
